<app-vault-module-shell moduleLabel="Registro de actividad" [accessDenied]="accessDenied()">
  <section class="audit">
    <app-page-header
      title="Registro de Actividad"
      [subtitle]="headerSubtitle()"
      icon="SearchCheck"
    >
      <span header-actions class="audit__header-actions">
        <app-export-format-selector
          label="Exportar"
          [options]="exportOptions"
          [disabled]="loading() || filteredLogs().length === 0"
          [loading]="exporting()"
          (exportSelected)="onExportFormatSelected($event)"
        />
        <lib-button
          variant="secondary"
          type="button"
          prefixIcon="RefreshCw"
          [loading]="loading()"
          (clicked)="reload()"
        >
          Actualizar
        </lib-button>
      </span>
    </app-page-header>

    <div class="audit__filters-panel">
      <div class="audit__filters-head">
        <h2 class="audit__filters-heading">Filtros</h2>
        <p class="audit__filters-desc">
          Los filtros se aplican al instante sobre los registros cargados (máx. {{ limitLabel }}).
          Use Actualizar registros para traer datos recientes del servidor.
        </p>
      </div>
      <app-grid-form preset="responsive" wrapperClass="audit__filters">
        @if (isSuperAdmin()) {
          <app-grid-form-item [span]="2">
            <app-field-label
              label="Empresa"
              tooltip="Filtra registros por empresa (solo superadministrador)."
            />
            <lib-select
              label=""
              [options]="tenantOptions()"
              [value]="selectedTenantId()"
              (valueChange)="onTenantChange($event)"
            />
          </app-grid-form-item>
        }
        <app-grid-form-item [span]="2">
          <app-field-label
            label="Módulo"
            tooltip="Área del sistema donde ocurrió la acción (custodia legal, retención documental, usuarios, etc.)."
          />
          <lib-select
            label=""
            [options]="entityTypeOptions()"
            [value]="entityTypeFilter()"
            (valueChange)="onEntityTypeChange($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="2">
          <app-field-label label="Usuario" tooltip="Usuarios de la empresa con actividad registrada o cuenta activa en el tenant." />
          <lib-select
            label=""
            [options]="userFilterOptions()"
            [value]="selectedUserId()"
            (valueChange)="onUserFilterChange($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="2">
          <app-field-label
            label="Acción"
            tooltip="Filtra por tipo de operación: crear, actualizar, eliminar, login o logout."
          />
          <lib-select
            label=""
            [options]="actionOptions"
            [value]="actionFilter()"
            (valueChange)="onActionChange($event)"
          />
        </app-grid-form-item>
      </app-grid-form>
      <div class="audit__filters-actions">
        <lib-button variant="primary" type="button" [loading]="loading()" (clicked)="applyServerFilters()">
          Actualizar registros
        </lib-button>
        <lib-button variant="secondary" type="button" [disabled]="loading()" (clicked)="clearClientFilters()">
          Limpiar filtros
        </lib-button>
      </div>
    </div>

    <section class="audit__stats" aria-label="Resumen de actividad">
      <article class="audit__stat-card">
        <h3>Total de Registros</h3>
        <p>{{ totalEvents() }}</p>
      </article>
      <article class="audit__stat-card">
        <h3>Creaciones</h3>
        <p class="is-create">{{ creates() }}</p>
      </article>
      <article class="audit__stat-card">
        <h3>Actualizaciones</h3>
        <p class="is-update">{{ updates() }}</p>
      </article>
      <article class="audit__stat-card">
        <h3>Eliminaciones</h3>
        <p class="is-delete">{{ deletes() }}</p>
      </article>
    </section>

    <section class="audit__log" aria-labelledby="audit-log-heading">
      <div class="audit__log-head">
        <h2 id="audit-log-heading" class="audit__log-heading">Registros de Actividad</h2>
        <p class="audit__log-meta">{{ totalEvents() }} registro(s) encontrado(s)</p>
      </div>

      @if (listError()) {
        <lib-alert
          type="error"
          title="No se pudieron actualizar los registros"
          [message]="listError()!"
          [closeButton]="true"
        />
      }

      <div class="audit__table-toolbar">
        <lib-search
          placeholder="Buscar en registros..."
          [searchTerm]="searchTerm()"
          (searchTermChange)="onSearchChange($event)"
          [loading]="loading()"
        />
      </div>

      @if (showInitialSkeleton()) {
        <div class="audit__skeleton" aria-busy="true">
          <lib-skeleton variant="rectangle" [height]="'48px'" />
          <lib-skeleton variant="rectangle" [height]="'48px'" />
          <lib-skeleton variant="rectangle" [height]="'48px'" />
        </div>
      }

      @if (showTable()) {
        <div class="audit__table-wrap" [class.is-loading]="loading()">
          <div class="audit__table-scroll">
          <table class="audit-table">
            <thead>
              <tr>
                <th class="audit-table__col-expand" scope="col" aria-label="Expandir"></th>
                <th scope="col">Fecha/Hora</th>
                <th scope="col">Usuario</th>
                <th scope="col">Acción</th>
                <th scope="col">Módulo</th>
                @if (isSuperAdmin()) {
                  <th scope="col">Empresa</th>
                }
                <th scope="col">IP / Ubicación</th>
                <th scope="col">Detalles</th>
              </tr>
            </thead>
            <tbody>
              @if (filteredLogs().length === 0) {
                <tr>
                  <td [attr.colspan]="tableColspan()" class="audit-table__empty">
                    <lib-icon name="Shield" [size]="48" class="audit-table__empty-icon" />
                    <p>No se encontraron registros de actividad</p>
                    @if (rawRows().length > 0) {
                      <p class="audit-table__empty-hint">Hay {{ rawRows().length }} registro(s) cargados; ajustá los filtros locales.</p>
                    }
                  </td>
                </tr>
              } @else {
                @for (log of paginatedLogs(); track log.id) {
                  <tr
                    class="audit-table__row"
                    [class.is-expanded]="isRowExpanded(log.id)"
                    (click)="toggleRowExpansion(log.id)"
                    (keydown.enter)="toggleRowExpansion(log.id)"
                    tabindex="0"
                    role="button"
                    [attr.aria-expanded]="isRowExpanded(log.id)"
                  >
                    <td class="audit-table__col-expand">
                      <lib-icon
                        [name]="isRowExpanded(log.id) ? 'ChevronDown' : 'ChevronRight'"
                        [size]="16"
                        class="audit-table__chevron-icon"
                      />
                    </td>
                    <td>
                      <div class="audit-table__cell-with-icon">
                        <lib-icon name="Calendar" [size]="16" class="audit-table__muted-icon" />
                        <div class="audit-table__date">
                          <strong>{{ formatDatePart(log.created_at) }}</strong>
                          <span class="audit-table__time">
                            <lib-icon name="Clock" [size]="12" class="audit-table__clock-icon" />
                            {{ formatTimePart(log.created_at) }}
                          </span>
                          <span class="audit-table__relative">{{ formatRelativeTime(log.created_at) }}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      @if (log.user) {
                        <div class="audit-table__cell-with-icon">
                          <lib-icon name="User" [size]="16" class="audit-table__muted-icon" />
                          <div class="audit-table__user">
                            <strong>{{ userDisplayName(log) }}</strong>
                            <span class="audit-table__muted">{{ userDisplayEmail(log) }}</span>
                            @if (isLoginAction(log.action)) {
                              <span class="audit-table__login-hint">
                                <lib-icon name="CircleCheck" [size]="12" class="audit-table__login-icon" />
                                Última conexión
                              </span>
                            }
                          </div>
                        </div>
                      } @else {
                        <span class="audit-table__muted">Usuario eliminado</span>
                      }
                    </td>
                    <td>
                      <div class="audit-table__action-cell">
                        <lib-icon
                          [name]="actionIconName(log.action)"
                          [size]="16"
                          [class]="actionIconClass(log.action)"
                        />
                        <span [class]="actionBadgeWrapClass(log.action)">
                          <lib-badge
                            [text]="actionBadgeLabel(log)"
                            [variant]="actionBadgeVariant(log.action)"
                            size="small"
                          />
                        </span>
                      </div>
                    </td>
                    <td>
                      @if (log.entity_type) {
                        <lib-badge [text]="auditModuleLabel(log.entity_type)" variant="neutral" size="small" />
                      } @else {
                        <span class="audit-table__muted">-</span>
                      }
                    </td>
                    @if (isSuperAdmin()) {
                      <td>
                        @if (isAuditLogSuperadminScope(log)) {
                          <span [attr.title]="superadminScopeTooltip">
                            <lib-badge text="Superadmin" variant="neutral" size="small" />
                          </span>
                        } @else if (auditTenantLabel(log); as tenantName) {
                          @if (tenantName !== '—') {
                            {{ tenantName }}
                          } @else {
                            <span class="audit-table__muted">-</span>
                          }
                        }
                      </td>
                    }
                    <td>
                      @if (log.ip_address) {
                        <div class="audit-table__cell-with-icon">
                          <lib-icon name="Globe" [size]="16" class="audit-table__muted-icon" />
                          <div class="audit-table__ip">
                            <code>{{ log.ip_address }}</code>
                            <span class="audit-table__ip-note">
                              <lib-icon name="MapPin" [size]="12" class="audit-table__muted-icon" />
                              IP registrada
                            </span>
                          </div>
                        </div>
                      } @else {
                        <span class="audit-table__muted">-</span>
                      }
                    </td>
                    <td>
                      @if (hasDetails(log)) {
                        <span class="audit-table__details-badge">
                          <lib-icon name="Info" [size]="12" class="audit-table__muted-icon" />
                          <lib-badge text="Ver detalles" variant="neutral" size="small" />
                        </span>
                      } @else {
                        <span class="audit-table__muted">-</span>
                      }
                    </td>
                  </tr>
                  @if (isRowExpanded(log.id)) {
                    <tr class="audit-table__expanded">
                      <td [attr.colspan]="tableColspan()">
                        <div class="audit-table__expanded-body">
                          <div class="audit-table__expanded-grid">
                            <div>
                              <div class="audit-table__field-label">ID del Registro</div>
                              <div class="audit-table__mono">{{ log.id }}</div>
                            </div>
                            @if (auditResourceIdFor(log); as resourceId) {
                              <div>
                                <div class="audit-table__field-label">ID del recurso</div>
                                <div class="audit-table__mono">{{ resourceId }}</div>
                              </div>
                            }
                            <div>
                              <div class="audit-table__field-label">Fecha Completa</div>
                              <div>{{ formatFullDate(log.created_at) }}</div>
                            </div>
                          </div>
                          @if (hasExpandedDetails(log)) {
                            <div>
                              <div class="audit-table__field-label">Información adicional</div>
                              <p class="audit-table__details-readable">{{ formatAuditDetails(log.details) }}</p>
                            </div>
                          }
                          @if (isLoginAction(log.action)) {
                            <p class="audit-table__login-note">
                              <lib-icon name="CircleCheck" [size]="16" class="audit-table__login-icon" />
                              Esta es la última conexión registrada para este usuario
                            </p>
                          }
                        </div>
                      </td>
                    </tr>
                  }
                }
              }
            </tbody>
          </table>
          </div>
          @if (filteredLogs().length > 0) {
            <div class="audit__pagination-wrapper">
              <mat-paginator
                [length]="filteredLogs().length"
                [pageSize]="pageSize()"
                [pageSizeOptions]="pageSizeOptions"
                [pageIndex]="pageIndex()"
                (page)="onPageChange($event)"
                showFirstLastButtons
                aria-label="Paginación del registro de actividad"
              />
            </div>
          }
        </div>
      }
    </section>
  </section>
</app-vault-module-shell>
